import React, {ReactNode, useCallback, useMemo, useState} from 'react'
import {FormSection} from "../../../common/components/SectionedForm";
import {api, buildPaginatedUrl} from "../../../common/api";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {ContactCenterCode, ServerPage} from "../../../app/types";
import {EventBusy, Summarize} from "@mui/icons-material";
import {isEnterpriseAdminSelector} from "../../../authentication/redux";
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid} from "@mui/material";
import {Sort, useInfiniteScroll} from "../../../common/components/table/useInfiniteScroll";
import {useAccessHelpers, useUrlHelpers} from "../../../common/util/hooks";
import {ColumnDefinition, InfiniteScrollTable} from "../../../common/components/table/Table";
import TableRowActionsMenu from "../../../common/components/table/TableRowActionsMenu";
import {sorterHeader} from "../../../common/components/table/SorterHeader";
import {AddButton} from "../../../common/components/AddButton";
import CancelButton from "../../../common/form/CancelButton";
import useContactCenterCodeForm from "./useContactCenterCodeForm";
import ErrorAlert from "../../../common/components/ErrorAlert";
import DeletionModal from "../../../common/components/DeletionModal";

export const useCallCenterCodeSection = (type: string) : FormSection<ContactCenterCode[]> => {
    const { t } = useTranslation()
    const {createUrl} = useUrlHelpers();
    const isEnterpriseAdmin = useSelector(isEnterpriseAdminSelector)
    const [entryToDelete, setEntryToDelete] = useState<ContactCenterCode | null>(null)
    const [entryToUpdate, setEntryToUpdate] = useState<ContactCenterCode | null>(null)
    const {hasWriteAccess, onlyWithWriteAccess} = useAccessHelpers()

    const fetchEntries = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<ContactCenterCode>> => {
            const { data } = await api.get(buildPaginatedUrl(createUrl('contact-center-codes'), pageParam, sortParam, searchTermParam) + `&type=${type}`)
            return data
        },
        [createUrl, type]
    )


    const { sort, setSort, rows, error, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteScroll<ContactCenterCode>({
        queryKey: `contact-center-code-${type}`,
        initialSort: { field: 'code', order: 'asc' },
        fetchFunction: fetchEntries,
    })

    const onSubmit = useCallback(
        async (formData: ContactCenterCode) => {
            const url = createUrl(`contact-center-codes${entryToUpdate?.uuid ? `/${entryToUpdate.uuid}` : ''}`)
            if (entryToUpdate?.uuid) {
                await api.put(url, {...formData, type: type})
            } else {
                await api.post(url, {...formData, type: type})
            }

            setEntryToUpdate(null)

            await refetch()
        },
        [refetch, setEntryToUpdate, entryToUpdate, createUrl, type]
    )

    const { handleSubmit, form: Form, submitButton: SubmitButton, reset, setValue } = useContactCenterCodeForm({ entry: entryToUpdate, onSubmit })

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: async (record: ContactCenterCode) => {
                    reset()
                    setEntryToUpdate(record)
                    setValue('code', record.code)
                    setValue('description', record.description)
                    setValue('type', record.type)
                    return false
                },
                dividerBeneath: true,
                needsWriteAccess: true
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: ContactCenterCode) => {
                    setEntryToDelete(record)
                },
                needsWriteAccess: true
            },
        ],
        [t, setValue, reset]
    )

    // @ts-ignore
    const columns: ColumnDefinition<ContactCenterCode>[] = useMemo(
        () => [
            {
                title: t('contact-center-codes.code'),
                key: 'code',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('contact-center-codes.description'),
                key: 'description',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            ...(isEnterpriseAdmin ?  [{
                    title: '',
                    key: 'uuid',
                    sorter: false,
                    render: (text: string, record: ContactCenterCode) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess}/>,
                }] : []
            ),
        ],
        [t, rowActions, isEnterpriseAdmin, hasWriteAccess]
    )

    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const getRowKey = useCallback(entry => entry.uuid, [])

    const getDeleteUrl = useCallback(i => i ? createUrl(`contact-center-codes/${i.uuid}`) : null, [createUrl])

    const buttonPanel = useMemo(
        () => (
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                {isEnterpriseAdmin &&
                    onlyWithWriteAccess(<Grid item>
                        <AddButton
                            onClick={() => {
                                reset()
                                setEntryToUpdate({ code: '', description: '', type: type, uuid: '' })
                                return null
                            }}
                            label={`${t('contact-center-codes.create-new')}`}
                        />
                    </Grid>)
                }
            </Grid>
        ),
        [t, reset, isEnterpriseAdmin, onlyWithWriteAccess, type]
    )

    const entryForm = useMemo(
        () => (
            <form className={'ant-form ant-form-vertical'} onSubmit={handleSubmit} noValidate={true}>
                <Dialog disablePortal open={!!entryToUpdate} onClose={() => setEntryToUpdate(null)}>
                    <DialogTitle>{t(entryToUpdate?.uuid ? 'contact-center-codes.update-dialog-title' : 'contact-center-codes.create-new')}</DialogTitle>
                    <DialogContent>
                        <Form />
                    </DialogContent>
                    <DialogActions>
                        <SubmitButton />
                        <CancelButton onClick={() => setEntryToUpdate(null)} />
                    </DialogActions>
                </Dialog>
            </form>
        ),
        [Form, SubmitButton, entryToUpdate, handleSubmit, t]
    )

    const renderDetails = useCallback(
        (data: ContactCenterCode[]): ReactNode => {
            return (
                <Grid container p={1}>
                    <Grid container xs={12} md={10} mb={3} item justifyContent={'flex-start'} flexDirection={'row'} flexWrap={'nowrap'}>
                        <Grid item>{buttonPanel}</Grid>
                    </Grid>
                    <ErrorAlert showAlert={!!error} />
                    <Grid item xs={12} md={10} >
                        <InfiniteScrollTable<ContactCenterCode>
                            hasNextPage={hasNextPage}
                            data={rows}
                            fetchNextPage={fetchNextPage}
                            isFetching={false}
                            getRowKey={getRowKey}
                            sorter={sorter}
                            columns={columns}
                            tableId={'phonebookTable'}
                        />
                    </Grid>

                    <DeletionModal<ContactCenterCode | null>
                        nameToDelete={cc => (`${cc?.code} ${cc?.description? `(${cc?.description})`: ''}`)}
                        itemToDelete={entryToDelete} deleteUrl={getDeleteUrl} onClose={() => setEntryToDelete(null)} onSuccess={refetch} />

                    {entryForm}
                </Grid>
            )
        },
        [ columns, buttonPanel, entryForm, entryToDelete, error, fetchNextPage, getDeleteUrl, getRowKey, hasNextPage, refetch, rows, sorter]
    )

    return {
        title: t(type === 'CDC' ?  'settings.cdc-codes' : 'settings.auc-codes'),
        data: rows,
        isFetching: isFetching || false,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: null,
        enabled: () => true,
        icon: () => type === 'CDC' ? <Summarize/> : <EventBusy/>
    }
}